import React, { useState } from 'react';
import { GeofenceInput, GeofenceAction } from '../services/GeofenceService';

export interface GeofenceFormProps {
  onSave: (data: GeofenceInput) => void;
  onCancel: () => void;
  initialData?: Partial<GeofenceInput>;
  ne: { lat: number; lng: number };
  sw: { lat: number; lng: number };
}

const INTERVAL_OPTIONS = [5, 10, 15, 20, 30];
const DURATION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const defaultAction = (): GeofenceAction => ({
  notify: false,
  rapidRefresh: {
    enabled: false,
    intervalSeconds: 10,
    durationMinutes: 5,
  },
});

const GeofenceForm: React.FC<GeofenceFormProps> = ({ onSave, onCancel, initialData, ne, sw }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [arrival, setArrival] = useState<GeofenceAction>(
    initialData?.actions?.arrival || defaultAction()
  );
  const [departure, setDeparture] = useState<GeofenceAction>(
    initialData?.actions?.departure || defaultAction()
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Name is required');
      return;
    }
    onSave({ name: trimmed, ne, sw, actions: { arrival, departure } });
  };

  const renderActionSection = (
    label: string,
    action: GeofenceAction,
    setAction: React.Dispatch<React.SetStateAction<GeofenceAction>>
  ) => (
    <fieldset style={{ border: '1px solid #ced4da', borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
      <legend style={{ fontWeight: 600, color: '#495057', fontSize: '1rem', padding: '0 4px' }}>
        {label}
      </legend>

      <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="checkbox"
          id={`${label}-notify`}
          checked={action.notify}
          onChange={(e) => setAction({ ...action, notify: e.target.checked })}
        />
        <label htmlFor={`${label}-notify`} style={{ marginBottom: 0, fontWeight: 500 }}>
          Send notification
        </label>
      </div>

      <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="checkbox"
          id={`${label}-rapid`}
          checked={action.rapidRefresh.enabled}
          onChange={(e) =>
            setAction({
              ...action,
              rapidRefresh: { ...action.rapidRefresh, enabled: e.target.checked },
            })
          }
        />
        <label htmlFor={`${label}-rapid`} style={{ marginBottom: 0, fontWeight: 500 }}>
          Enable rapid refresh
        </label>
      </div>

      {action.rapidRefresh.enabled && (
        <div style={{ marginLeft: 24, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div className="form-group">
            <label htmlFor={`${label}-interval`}>Interval</label>
            <select
              id={`${label}-interval`}
              value={action.rapidRefresh.intervalSeconds}
              onChange={(e) =>
                setAction({
                  ...action,
                  rapidRefresh: { ...action.rapidRefresh, intervalSeconds: Number(e.target.value) },
                })
              }
              style={{ padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 16 }}
            >
              {INTERVAL_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}s</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor={`${label}-duration`}>Duration</label>
            <select
              id={`${label}-duration`}
              value={action.rapidRefresh.durationMinutes}
              onChange={(e) =>
                setAction({
                  ...action,
                  rapidRefresh: { ...action.rapidRefresh, durationMinutes: Number(e.target.value) },
                })
              }
              style={{ padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 16 }}
            >
              {DURATION_OPTIONS.map((m) => (
                <option key={m} value={m}>{m} min</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </fieldset>
  );

  return (
    <div className="object-form">
      <h3>Configure Geofence</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="geofence-name">Name *</label>
          <input
            id="geofence-name"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(null); }}
            placeholder="Enter geofence name"
          />
          {nameError && <div className="validation-error">{nameError}</div>}
        </div>

        {renderActionSection('Arrival', arrival, setArrival)}
        {renderActionSection('Departure', departure, setDeparture)}

        <div className="form-actions">
          <button type="submit" className="btn-primary">Save</button>
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default GeofenceForm;
